import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { Outlet } from '@/lib/router-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import api from '../lib/api';
import {
  aiExecutionKey,
  useAiTriggerExecutionStore,
} from '../stores/ai-trigger-execution.store';
import { useEventBusStore } from '../stores/event-bus.store';
import { Breadcrumb } from '../components/Breadcrumb';
import { ArchiveProcessModal } from '../components/ArchiveProcessModal';
import { ProcessFormModal } from '../components/ProcessFormModal';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import { ConvertCasoToProcessDto, ProcessPartDto, UpdateProcessDto } from '@prometeus/contracts';
import { EditIcon } from '../components/icons/EditIcon';
import { ArchiveIcon } from '../components/icons/ArchiveIcon';
import { ProcessAiModal } from '../components/process/ProcessAiModal';
import { ConvertCasoModal } from '../components/ConvertCasoModal';

export const Route = createFileRoute('/w/$workspaceId/clients/$clientId/processes/$processId')({
  component: ProcessDetailLayout,
});


function ProcessDetailLayout() {
  const { workspaceId, clientId, processId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { requirePermission } = usePermissionGuard();
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);
  const queryClient = useQueryClient();
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const addToast = useEventBusStore((state) => state.addToast);

  const canRead = hasPermission('processes.read');

  /** Uma única fonte de GET active + fallback lento; WebSocket é o caminho principal. */
  const PROCESS_AI_POLL_MS = 12_000;
  const processAiActivePollKey = useAiTriggerExecutionStore((s) => {
    const kAn = aiExecutionKey(workspaceId, 'analise_processo_ia', 'process', processId);
    const kHi = aiExecutionKey(workspaceId, 'historico_processo_ia', 'process', processId);
    const idAn = s.byKey[kAn]?.executionId;
    const idHi = s.byKey[kHi]?.executionId;
    return [idAn, idHi].filter(Boolean).join('|') || null;
  });

  useEffect(() => {
    if (!canRead) return;
    let cancelled = false;
    const syncProcessAiActive = async () => {
      try {
        const store = useAiTriggerExecutionStore.getState();
        const kAn = aiExecutionKey(workspaceId, 'analise_processo_ia', 'process', processId);
        const kHi = aiExecutionKey(workspaceId, 'historico_processo_ia', 'process', processId);
        const hadRunning = !!(store.byKey[kAn] || store.byKey[kHi]);

        const r = await api.get(
          `/clients/${clientId}/processes/${processId}/ai-trigger-executions/active`,
          { headers: { 'x-workspace-id': workspaceId } },
        );
        const rows = r.data?.data ?? r.data;
        if (cancelled || !Array.isArray(rows)) return;

        useAiTriggerExecutionStore.getState().bootstrap(workspaceId, rows, {
          resourceType: 'process',
          resourceId: processId,
        });

        if (hadRunning && rows.length === 0) {
          queryClient.invalidateQueries({
            queryKey: ['processes', workspaceId, clientId, processId],
          });
        }
      } catch {
        /* ignore */
      }
    };

    void syncProcessAiActive();
    let timer: ReturnType<typeof setInterval> | undefined;
    if (processAiActivePollKey) {
      timer = setInterval(() => {
        void syncProcessAiActive();
      }, PROCESS_AI_POLL_MS);
    }
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [
    canRead,
    workspaceId,
    clientId,
    processId,
    queryClient,
    processAiActivePollKey,
  ]);

  useEffect(() => {
    if (!location.pathname.startsWith('/w/')) return;
    if (!canRead) {
      showPermissionDenied('Ver processos');
      navigate({ to: '/w/$workspaceId/clients/$clientId', params: { workspaceId, clientId } });
    }
  }, [location.pathname, canRead, showPermissionDenied, navigate, workspaceId, clientId]);

  const updateProcessMutation = useMutation({
    mutationFn: async (data: UpdateProcessDto) => {
      const response = await api.put(`/clients/${clientId}/processes/${processId}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processes', workspaceId, clientId, processId] });
      queryClient.invalidateQueries({ queryKey: ['processes', workspaceId, clientId] });
      setShowEditModal(false);
    },
    onError: (error: any) => {
      console.error('Erro ao atualizar processo:', error);
      alert(error.response?.data?.message || 'Erro ao atualizar processo');
    },
  });

  const unarchiveProcessMutation = useMutation({
    mutationFn: async () => {
      const response = await api.put(`/clients/${clientId}/processes/${processId}/unarchive`);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processes', workspaceId, clientId, processId] });
      queryClient.invalidateQueries({ queryKey: ['processes', workspaceId, clientId] });
    },
    onError: (error: any) => {
      console.error('Erro ao ativar processo:', error);
      alert(error.response?.data?.message || 'Erro ao ativar processo');
    },
  });

  const convertToProcessMutation = useMutation({
    mutationFn: async (dto: ConvertCasoToProcessDto) => {
      const response = await api.post(`/clients/${clientId}/processes/${processId}/convert-to-process`, dto);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processes', workspaceId, clientId, processId] });
      queryClient.invalidateQueries({ queryKey: ['processes', workspaceId, clientId] });
      setShowConvertModal(false);
      addToast('Caso convertido em processo com sucesso', 'success');
    },
    onError: (error: any) => {
      addToast(error.response?.data?.message || 'Erro ao converter caso em processo', 'error');
    },
  });

  const handleSaveProcess = (data: any) => {
    if (!requirePermission('processes.update', 'Editar processos')) return;
    const updateData: UpdateProcessDto = { ...data };
    updateProcessMutation.mutate(updateData);
  };

  const { data: process, isLoading: isLoadingProcess, error: processError } = useQuery({
    queryKey: ['processes', workspaceId, clientId, processId],
    queryFn: async () => {
      const response = await api.get(`/clients/${clientId}/processes/${processId}`);
      return response.data.data;
    },
    enabled: canRead,
  });

  const { data: client } = useQuery({
    queryKey: ['clients', workspaceId, clientId],
    queryFn: async () => {
      const response = await api.get(`/clients/${clientId}`);
      return response.data.data;
    },
    enabled: !!clientId,
  });

  const { data: workspace } = useQuery({
    queryKey: ['workspaces', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data.data;
    },
    enabled: !!workspaceId,
  });

  const { data: members } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: async () => {
      const response = await api.get('/members');
      return response.data.data;
    },
    enabled: !!workspaceId,
  });

  // Buscar o membro responsável pelo processo
  const responsibleMember = process?.responsibleMemberId 
    ? members?.find((m: any) => m.id === process.responsibleMemberId)
    : null;

  const isTasksTab = location.pathname.endsWith('/tasks');
  const isSyncHistoryTab = location.pathname.endsWith('/sync-history');
  const isFinanceiroTab = location.pathname.endsWith('/financeiro');
  const isIndexTab = !isTasksTab && !isSyncHistoryTab && !isFinanceiroTab;

  if (!canRead) return null;

  if (isLoadingProcess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div
            className="bg-white rounded-[15px] p-6 animate-pulse"
            style={{ fontFamily: 'Poppins, sans-serif', boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)' }}
          >
            <div className="space-y-4">
              <div className="h-6 rounded w-1/4" style={{ backgroundColor: 'rgba(6, 20, 27, 0.08)' }}></div>
              <div className="h-4 rounded w-1/2" style={{ backgroundColor: 'rgba(6, 20, 27, 0.06)' }}></div>
            </div>
            <p className="mt-4" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#9BA8AB' }}>
              Carregando processo...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (processError) {
    console.error('Erro ao carregar processo:', processError);
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div
            className="bg-white rounded-[15px] p-6"
            style={{ fontFamily: 'Poppins, sans-serif', boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)' }}
          >
            <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#06141B' }}>
              Erro ao carregar informações do processo
            </p>
            <pre className="mt-4 text-xs p-4 rounded-[10px] overflow-auto" style={{ backgroundColor: 'rgba(6, 20, 27, 0.06)', color: '#2D3748', fontFamily: 'Poppins, sans-serif' }}>
              {JSON.stringify(processError, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  if (!process) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div
            className="bg-white rounded-[15px] p-6"
            style={{ fontFamily: 'Poppins, sans-serif', boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)' }}
          >
            <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#2D3748' }}>Processo não encontrado</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: workspace?.name || 'Workspace', to: '/w/$workspaceId/clients', params: { workspaceId } },
            { label: 'Clientes', to: '/w/$workspaceId/clients', params: { workspaceId } },
            { label: client?.name || 'Cliente', to: '/w/$workspaceId/clients/$clientId', params: { workspaceId, clientId } },
            { label: 'Processos', to: '/w/$workspaceId/clients/$clientId/processes', params: { workspaceId, clientId } },
            { label: process?.processNumber || 'Processo' },
          ]}
        />

        {/* Container do cabeçalho do processo */}
        <div
          className="mt-6 bg-white rounded-[15px] p-5"
          style={{
            fontFamily: 'Poppins, sans-serif',
            boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Layout compacto */}
          <div className="flex items-center justify-between">
            {/* Lado esquerdo: Nome dos envolvidos e número do processo */}
            <div className="flex items-center gap-3 flex-1">
              <div className="flex flex-col">
                {/* Nome dos envolvidos - acima do número */}
                {(() => {
                  if (!process.parts || process.parts.length === 0) {
                    return process.parties ? (
                      <h1 className="text-[18px] mb-1" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, color: '#06141B' }}>
                        {process.parties}
                      </h1>
                    ) : null;
                  }

                  const clientPole = process.clientPole || '';
                  const activeParts = process.parts.filter((p: ProcessPartDto) => p.pole === 'ativo');
                  const passiveParts = process.parts.filter((p: ProcessPartDto) => p.pole === 'passivo');
                  
                  // Assumir que o cliente é sempre a primeira parte no seu polo
                  const clientActivePart = clientPole === 'ativo' ? activeParts[0] : null;
                  const clientPassivePart = clientPole === 'passivo' ? passiveParts[0] : null;
                  
                  // Contar outras partes (excluindo o cliente)
                  const otherActiveParts = clientPole === 'ativo' ? activeParts.slice(1) : activeParts;
                  const otherPassiveParts = clientPole === 'passivo' ? passiveParts.slice(1) : passiveParts;
                  const otherParts = process.parts.filter((p: ProcessPartDto) => !['ativo', 'passivo'].includes(p.pole));
                  
                  let partsDisplay = '';
                  
                  if (clientPole === 'ativo' && clientActivePart) {
                    // Cliente no polo ativo: apenas ele antes do VS
                    partsDisplay = clientActivePart.name;
                    
                    // Se houver outras partes ativas OU partes passivas, adicionar "..."
                    if (otherActiveParts.length > 0 || passiveParts.length > 0 || otherParts.length > 0) {
                      partsDisplay += ' ...';
                    }
                    
                    // Se houver partes passivas ou outras, adicionar VS
                    if (passiveParts.length > 0 || otherParts.length > 0) {
                      partsDisplay += ' vs ';
                      if (passiveParts.length > 0) {
                        partsDisplay += passiveParts[0].name;
                        if (passiveParts.length > 1 || otherParts.length > 0) {
                          partsDisplay += ' ...';
                        }
                      } else if (otherParts.length > 0) {
                        partsDisplay += otherParts[0].name;
                        if (otherParts.length > 1) {
                          partsDisplay += ' ...';
                        }
                      }
                    }
                  } else if (clientPole === 'passivo' && clientPassivePart) {
                    // Cliente no polo passivo: primeiro ativo antes do VS, depois cliente
                    if (activeParts.length > 0) {
                      partsDisplay = activeParts[0].name;
                      // Se houver mais partes ativas, adicionar "..."
                      if (activeParts.length > 1) {
                        partsDisplay += ' ...';
                      }
                      partsDisplay += ' vs ';
                    }
                    
                    // Adicionar o cliente
                    partsDisplay += clientPassivePart.name;
                    
                    // Se houver outras partes passivas ou outras, adicionar "..."
                    if (otherPassiveParts.length > 0 || otherParts.length > 0) {
                      partsDisplay += ' ...';
                    }
                  } else {
                    // Cliente sem polo definido ou outro polo: primeiro ativo, VS, primeiro passivo
                    if (activeParts.length > 0) {
                      partsDisplay = activeParts[0].name;
                      if (activeParts.length > 1 || passiveParts.length > 0 || otherParts.length > 0) {
                        partsDisplay += ' ...';
                      }
                    }
                    
                    if (passiveParts.length > 0 || otherParts.length > 0) {
                      if (partsDisplay) partsDisplay += ' vs ';
                      if (passiveParts.length > 0) {
                        partsDisplay += passiveParts[0].name;
                        if (passiveParts.length > 1 || otherParts.length > 0) {
                          partsDisplay += ' ...';
                        }
                      } else if (otherParts.length > 0) {
                        partsDisplay += otherParts[0].name;
                        if (otherParts.length > 1) {
                          partsDisplay += ' ...';
                        }
                      }
                    }
                  }

                  return partsDisplay ? (
                    <h1 className="text-[18px] mb-1" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, color: '#06141B' }}>
                      {partsDisplay}
                    </h1>
                  ) : null;
                })()}
                {/* Número do processo */}
                {process.type !== 'caso' && (
                  <p className="text-[14px]" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400, color: '#06141B' }}>
                    {process.processNumber}
                  </p>
                )}
                {/* Membro Responsável */}
                {responsibleMember && (
                  <span className="text-[12px] mt-1 block" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400, color: '#9BA8AB' }}>
                    Responsável: {responsibleMember.user?.name || 'N/A'}
                  </span>
                )}
                {process.court && (
                  <span className="text-[12px] mt-1 block" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400, color: '#9BA8AB' }}>
                    Tribunal: {process.court}
                  </span>
                )}
              </div>
            </div>

            {/* Lado direito: Status, Polo e Botões */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {/* Badge caso - segue o mesmo padrão visual das outras tags */}
                {process.type === 'caso' && (
                  <span
                    className="px-2.5 py-1 rounded-[10px] whitespace-nowrap"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: 10,
                      fontWeight: 400,
                      backgroundColor: '#EBF8FF',
                      color: '#2B6CB0',
                    }}
                  >
                    caso
                  </span>
                )}
                <span
                  className="px-2.5 py-1 rounded-[10px] whitespace-nowrap"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 10,
                    fontWeight: 400,
                    ...(process.status === 'ativo' || process.status === 'Ativo'
                      ? { backgroundColor: '#10B98120', color: '#10B981' }
                      : process.status === 'arquivado' || process.status === 'Arquivado'
                      ? { backgroundColor: '#71809620', color: '#718096' }
                      : { backgroundColor: '#3182CE20', color: '#3182CE' }),
                  }}
                >
                  {process.status}
                </span>
                {/* Polo do cliente - ao lado do status */}
                {process.clientPole && (
                  <span
                    className="px-2.5 py-1 rounded-[10px] whitespace-nowrap"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: 10,
                      fontWeight: 400,
                      backgroundColor: '#71809620',
                      color: '#718096',
                    }}
                  >
                    Polo: {process.clientPole}
                  </span>
                )}
                {process.archivedAt && (
                  <>
                    <span
                      className="px-2.5 py-1 rounded-[10px] whitespace-nowrap flex items-center gap-1"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: 10,
                        fontWeight: 400,
                        backgroundColor: '#71809620',
                        color: '#718096',
                      }}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                      </svg>
                      Arquivado
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        if (!requirePermission('processes.update', 'Editar processos')) return;
                        unarchiveProcessMutation.mutate();
                      }}
                      disabled={unarchiveProcessMutation.isPending}
                      className="px-4 py-2 rounded-[10px] transition-opacity hover:opacity-90 flex items-center gap-2 disabled:opacity-50"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: 12,
                        backgroundColor: '#06141B',
                        color: '#F9FAFB',
                      }}
                    >
                      {unarchiveProcessMutation.isPending ? 'Ativando...' : 'Ativar processo'}
                    </button>
                  </>
                )}
              </div>
              {!process.archivedAt && (
                <div className="flex gap-2 items-center">
                  {hasPermission('ai.use_credits') && (
                    <button
                      type="button"
                      onClick={() => setShowAiModal(true)}
                      className="flex h-9 w-9 shrink-0 items-center justify-center border-0 p-0 text-[#06141B] transition-opacity hover:opacity-90 outline-none"
                      style={{
                        borderRadius: 5,
                        backgroundColor: '#D9D9D9',
                        boxShadow: '0 0 15px rgba(0, 0, 0, 0.07)',
                      }}
                      title="Analisar com IA"
                      aria-label="Analisar processo com IA"
                    >
                      <svg
                        width={18}
                        height={18}
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden
                      >
                        <path
                          d="M14 0C13.316 2.472 12.435 3.3165 10 4C12.435 4.6835 13.316 5.528 14 8C14.684 5.528 15.565 4.6835 18 4C15.565 3.3165 14.684 2.472 14 0ZM7 4C5.8025 8.3255 4.262 9.804 0 11C4.262 12.196 5.8025 13.6745 7 18C8.1975 13.6745 9.738 12.196 14 11C9.738 9.804 8.1975 8.3255 7 4Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => navigate({ to: '/w/$workspaceId/clients/$clientId/documents', params: { workspaceId, clientId }, search: { folder: processId } })}
                    className="px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '12px',
                      backgroundColor: '#06141B',
                      color: '#F9FAFB',
                      borderRadius: 10,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path opacity="0.3" d="M28.0783 8.5C28.4897 8.50016 28.8962 8.58991 29.2694 8.76303C29.6426 8.93614 29.9737 9.18847 30.2395 9.50246C30.5054 9.81645 30.6997 10.1846 30.8089 10.5812C30.9181 10.9779 30.9396 11.3936 30.872 11.7994L28.5118 25.9661C28.4015 26.6277 28.06 27.2286 27.5482 27.6621C27.0364 28.0956 26.3874 28.3334 25.7167 28.3333H8.2832C7.6125 28.3334 6.96351 28.0956 6.4517 27.6621C5.93989 27.2286 5.59845 26.6277 5.48812 25.9661L3.12795 11.7994C3.06027 11.3936 3.08179 10.9779 3.191 10.5812C3.30021 10.1846 3.4945 9.81645 3.76036 9.50246C4.02622 9.18847 4.35727 8.93614 4.73051 8.76303C5.10374 8.58991 5.51019 8.50016 5.92162 8.5H28.0783Z" fill="#F9FAFB"/>
                      <path d="M25.5002 4.25C25.8759 4.25 26.2362 4.39926 26.5019 4.66493C26.7676 4.93061 26.9168 5.29094 26.9168 5.66667C26.9168 6.04239 26.7676 6.40272 26.5019 6.6684C26.2362 6.93408 25.8759 7.08333 25.5002 7.08333H8.50016C8.12444 7.08333 7.7641 6.93408 7.49843 6.6684C7.23275 6.40272 7.0835 6.04239 7.0835 5.66667C7.0835 5.29094 7.23275 4.93061 7.49843 4.66493C7.7641 4.39926 8.12444 4.25 8.50016 4.25H25.5002Z" fill="#F9FAFB"/>
                    </svg>
                    Documentos
                  </button>
                  {process.type === 'caso' && hasPermission('processes.convert') && (
                    <button
                      type="button"
                      onClick={() => setShowConvertModal(true)}
                      disabled={convertToProcessMutation.isPending}
                      className="px-4 py-2 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '12px',
                        backgroundColor: '#276749',
                        color: '#F9FAFB',
                        borderRadius: 10,
                      }}
                    >
                      Tornar Processo
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (!requirePermission('processes.update', 'Editar processos')) return;
                      setShowEditModal(true);
                    }}
                    className="px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '12px',
                      backgroundColor: '#06141B',
                      color: '#F9FAFB',
                      borderRadius: 10,
                    }}
                  >
                    <EditIcon className="w-4 h-4" />
                    Editar
                  </button>
                  <button
                    onClick={() => {
                      if (!requirePermission('processes.update', 'Arquivar processos')) return;
                      setShowArchiveModal(true);
                    }}
                    className="px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '12px',
                      backgroundColor: '#06141B',
                      color: '#F9FAFB',
                      borderRadius: 10,
                    }}
                  >
                    <ArchiveIcon className="w-4 h-4" />
                    Arquivar
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sistema de Abas */}
          <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(6, 20, 27, 0.08)' }}>
            <nav className="flex space-x-4">
              <button
                onClick={() => navigate({ to: `/w/${workspaceId}/clients/${clientId}/processes/${processId}/` })}
                className={`${isIndexTab ? 'border-b-2' : ''} whitespace-nowrap py-2 px-1 font-medium transition-colors`}
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px',
                  color: isIndexTab ? '#06141B' : '#9BA8AB',
                  borderBottomColor: isIndexTab ? '#06141B' : 'transparent',
                }}
              >
                Informações
              </button>
              <button
                onClick={() => navigate({ to: `/w/${workspaceId}/clients/${clientId}/processes/${processId}/tasks` })}
                className={`${isTasksTab ? 'border-b-2' : ''} whitespace-nowrap py-2 px-1 font-medium transition-colors`}
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px',
                  color: isTasksTab ? '#06141B' : '#9BA8AB',
                  borderBottomColor: isTasksTab ? '#06141B' : 'transparent',
                }}
              >
                Tarefas
              </button>
              <button
                onClick={() => navigate({ to: `/w/${workspaceId}/clients/${clientId}/processes/${processId}/sync-history` })}
                className={`${isSyncHistoryTab ? 'border-b-2' : ''} whitespace-nowrap py-2 px-1 font-medium transition-colors`}
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px',
                  color: isSyncHistoryTab ? '#06141B' : '#9BA8AB',
                  borderBottomColor: isSyncHistoryTab ? '#06141B' : 'transparent',
                }}
              >
                Histórico
              </button>
              {hasPermission('financeiro.read') && (
                <button
                  onClick={() => navigate({ to: `/w/${workspaceId}/clients/${clientId}/processes/${processId}/financeiro` })}
                  className={`${isFinanceiroTab ? 'border-b-2' : ''} whitespace-nowrap py-2 px-1 font-medium transition-colors`}
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '12px',
                    color: isFinanceiroTab ? '#06141B' : '#9BA8AB',
                    borderBottomColor: isFinanceiroTab ? '#06141B' : 'transparent',
                  }}
                >
                  Financeiro
                </button>
              )}
            </nav>
          </div>
        </div>


        {/* Renderiza as rotas filhas (abas) */}
        <div>
          <Outlet />
        </div>

        {/* Modal de Arquivamento */}
        <ArchiveProcessModal
          isOpen={showArchiveModal}
          onClose={() => setShowArchiveModal(false)}
          processId={processId}
          clientId={clientId}
          workspaceId={workspaceId}
        />

        {/* Modal de Edição */}
        {showEditModal && process && (
          <ProcessFormModal
            workspaceId={workspaceId}
            clientId={clientId}
            processId={processId}
            process={process}
            onClose={() => setShowEditModal(false)}
            onSave={handleSaveProcess}
            isSaving={updateProcessMutation.isPending}
          />
        )}

        {/* Modal de Conversão de Caso em Processo */}
        {showConvertModal && (
          <ConvertCasoModal
            onClose={() => setShowConvertModal(false)}
            onConfirm={(dto) => convertToProcessMutation.mutate(dto)}
            isSaving={convertToProcessMutation.isPending}
          />
        )}

        {/* Modal de Análise com IA */}
        <ProcessAiModal
          open={showAiModal}
          onClose={() => setShowAiModal(false)}
          clientId={clientId}
          processId={processId}
          workspaceId={workspaceId}
          lastAiAnalysisAt={process.lastAiAnalysisAt}
          lastAiAnalysisResult={process.lastAiAnalysisResult}
          lastAiHistoryAt={process.lastAiHistoryAt}
          lastAiHistoryResult={process.lastAiHistoryResult}
        />
      </div>
    </div>
  );
}
